import supabase from 'lib/supabase-browser'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Team } from 'lib/types/generated-types'

export const useTeam = ({ teamId }: { teamId: number }) => {
  return useQuery(
    ['team', teamId],
    async () => {
      const { data, error } = await supabase
        .from('teams')
        .select('*')
        .eq('id', teamId)
        .single()
      if (error) throw error
      return data
    },
    {
      enabled: !!teamId && teamId !== -1,
    }
  )
}

export const useTeamByOwner = ({ owner }: { owner: string }) => {
  return useQuery(
    ['team', owner],
    async () => {
      const { data, error } = await supabase
        .from('teams')
        .select('*')
        .eq('owner', owner)
        .maybeSingle()
      if (error) throw error
      return data
    },
    {
      enabled: !!owner,
    }
  )
}

export const useTeamsWhereMember = ({ userId }: { userId: string }) => {
  return useQuery(
    ['teams', userId],
    async () => {
      const { data, error } = await supabase
        .from('team_members')
        .select('team_id')
        .eq('user_id', userId)
        .not('role', 'eq', 'owner')
      if (error) throw error

      if (!data || data.length === 0) return []

      const teamIds = data.map((member: { team_id: number }) => member.team_id)

      const { data: teams, error: teamsError } = await supabase
        .from('teams')
        .select('*')
        .in('id', teamIds)
      if (teamsError) throw teamsError

      return teams
    },
    {
      enabled: !!userId,
    }
  )
}

export const useTeamsWhereMemberAndSeatsGreaterThanOne = ({
  userId,
}: {
  userId: string
}) => {
  return useQuery(['user_teams', userId], async () => {
    // get all teams where user is team member
    const { data: teamIds, error } = await supabase
      .from('team_members')
      .select('team_id')
      .eq('user_id', userId)
    if (error) throw error

    if (!teamIds || teamIds.length === 0) return []

    const teamIdsArray = teamIds.map(
      (member: { team_id: number }) => member.team_id
    )

    const { data: teams, error: teamsError } = await supabase
      .from('teams')
      .select('*')
      .in('id', teamIdsArray)
      .gte('seats', 2)
    if (teamsError) throw teamsError

    return teams
  })
}

export const useUpdateTeam = () => {
  const queryClient = useQueryClient()
  return useMutation(
    ['update_team'],
    async (team: Team) => {
      const { data, error } = await supabase
        .from('teams')
        .update(team)
        .eq('id', team.id)
        .select('*')
        .single()
      if (error) throw error
      return data
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['team', data.id])
      },
    }
  )
}
